import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SectionTitle } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { errorMessage } from '@/lib/api';
import { notificationPrefsApi, type NotificationPreferences } from '@/lib/reports';

const KEY = ['me', 'notifications'] as const;

/** Account → Notifications: report emails / in-app copies on or off, and the reports this person muted. */
export function NotificationsSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => notificationPrefsApi.get() });
  const save = useMutation({
    mutationFn: (body: Partial<Pick<NotificationPreferences, 'reportEmails' | 'reportInApp' | 'mutedReportIds'>>) => notificationPrefsApi.update(body),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      toast.success('Notification preferences saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const p = q.data;
  return (
    <section id="notifications" className="bg-card border p-6">
      <SectionTitle className="mb-2">Notifications</SectionTitle>
      <p className="text-muted-foreground mb-4 text-sm">Reports that Super Users or job admins send to you. Turning these off never affects sign-in codes or invitations.</p>
      <div className="space-y-3">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            <span className="font-medium">Email me reports</span>
            <span className="text-muted-foreground block text-xs">Digests and notices arrive in your inbox.</span>
          </span>
          <Switch checked={p?.reportEmails ?? true} disabled={!p || save.isPending} onCheckedChange={(v) => save.mutate({ reportEmails: v })} />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            <span className="font-medium">Show reports in the app</span>
            <span className="text-muted-foreground block text-xs">Under Reports, with an unread badge.</span>
          </span>
          <Switch checked={p?.reportInApp ?? true} disabled={!p || save.isPending} onCheckedChange={(v) => save.mutate({ reportInApp: v })} />
        </label>
        {p && p.mutedReports.length > 0 && (
          <div className="border-t pt-3">
            <div className="mb-2 text-sm font-medium">Muted reports</div>
            <ul className="space-y-1">
              {p.mutedReports.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 text-sm">
                  <span>{m.name}</span>
                  <Button variant="ghost" size="sm" disabled={save.isPending} onClick={() => save.mutate({ mutedReportIds: p.mutedReportIds.filter((id) => id !== m.id) })}>
                    Unmute
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
